<template>
    <base-panel
        :panel="panel"
        :review-mode="reviewMode"
        v-on:continue="handleContinue()"
        v-on:submit="handleSubmit()"
        v-on:edit-action="handleEditAction($event)"
        v-on:update-width="handleUpdateWidth($event)"
        ref="basePanel"
    >
        <template slot="content">
            <div class="d-flex justify-content-center align-items-center flex-grow-1">
                <div>
                    <div v-show="!reviewMode">
                        <!-- Time Remaining -->
                        <div
                            class="h5 font-weight-500 text-secondary d-flex justify-content-center"
                            :class="{ 'opacity-0': currentState !== 'isStarted' }"
                        >
                            {{ timeRemainingFormatted }}
                        </div>
                        <!-- Speech Audio Visualizer -->
                        <div v-show="currentState === 'isStarted'" class="visualizer-container mt-2">
                            <canvas ref="canvas" width="160" height="160"></canvas>
                        </div>
                        <!-- Start Conversation Button -->
                        <div
                            class="mt-2 d-flex align-items-center justify-content-center"
                            v-if="currentState !== 'isStarted'"
                            style="height: 160px"
                        >
                            <div
                                class="record-btn-container"
                                :class="{
                                    'record-btn-lg': currentState === 'preInit',
                                    'record-btn-md': currentState === 'isInitializing',
                                    'record-btn-sm': currentState === 'isStarting',
                                }"
                            >
                                <button
                                    @click="initConversation()"
                                    class="btn btn-primary px-2 py-2 d-flex align-items-center justify-content-center rounded-circle record-btn"
                                    :class="{
                                        'pulse-primary': currentState === 'preInit',
                                    }"
                                    :disabled="currentState !== 'preInit'"
                                >
                                    <div class="d-flex flex-column justify-content-center">
                                        <div v-if="currentState === 'isInitializing'" class="spinner">
                                            <span class="sr-only">Loading...</span>
                                        </div>
                                        <span
                                            v-else
                                            class="icon icon-glyphicons-basic-equalizer-bars record-btn-icon"
                                        ></span>
                                    </div>
                                </button>
                            </div>
                        </div>
                        <!-- Status Readouts & Toolbar Items -->
                        <div class="d-flex justify-content-center" style="height: 75px">
                            <div v-if="currentState === 'preInit'" class="text-secondary text-center pt-3">
                                <span>Press the button above to start the conversation</span>
                            </div>
                            <div
                                v-else-if="['isStarting', 'isInitializing'].includes(currentState)"
                                class="text-secondary pt-3"
                            >
                                Initializing...
                            </div>
                            <!-- Conversation Toolbar Items -->
                            <div v-else-if="currentState === 'isStarted'" class="d-flex align-items-center">
                                <div class="p-2 bg-offwhite rounded-lg">
                                    <button
                                        class="btn btn-transparent p-1 mr-1"
                                        @click="endConversation()"
                                        :disabled="!allowStopConversation"
                                    >
                                        <span class="icon icon-glyphicons-basic-stop text-danger mr-1"></span>
                                        <span>End<span class="d-none d-sm-inline">&nbsp;Conversation</span></span>
                                    </button>
                                    <button
                                        class="btn btn-transparent p-1"
                                        @click="setMicMuteState(true)"
                                        v-show="!micIsMuted"
                                    >
                                        <span class="icon icon-glyphicons-basic-mic mr-1"></span>
                                        <span>Mute</span>
                                    </button>
                                    <button
                                        class="btn btn-transparent p-1"
                                        @click="setMicMuteState(false)"
                                        v-show="micIsMuted"
                                    >
                                        <span class="icon icon-glyphicons-basic-mic-off text-danger mr-1"></span>
                                        <span>Unmute</span>
                                    </button>
                                </div>
                                <div class="d-flex align-items-center ml-3 pl-2">
                                    <div
                                        class="active-marker rounded-circle mr-2"
                                        :class="{ 'bg-secondary': showAiSpeaking, 'bg-primary': !showAiSpeaking }"
                                    ></div>
                                    <div
                                        class="readout"
                                        :class="{ 'text-secondary': !showAiSpeaking, 'text-secondary': showAiSpeaking }"
                                        style="width: 100px"
                                    >
                                        <span v-show="showAiSpeaking">Their turn</span>
                                        <span v-show="!showAiSpeaking">Your turn</span>
                                    </div>
                                </div>
                            </div>
                            <div
                                v-else-if="currentState === 'isFailed'"
                                class="text-secondary pt-1 d-flex flex-column align-items-center"
                            >
                                <div class="mb-1">Failed to start conversation</div>
                                <button class="btn btn-link p-1" @click="initConversation()">
                                    <span class="icon icon-100 icon-glyphicons-basic-refresh mr-1"></span>
                                    <span>Retry</span>
                                </button>
                            </div>
                            <div v-else-if="submitIsAnalyzing">
                                <div class="small d-flex justify-content-center">
                                    <span class="font-weight-500 flex-grow-0 text-light-wave"
                                        >Analyzing your conversation...</span
                                    >
                                </div>
                            </div>
                            <div v-else-if="currentState === 'isEnded'" class="text-secondary pt-3">Conversation ended</div>
                            <div v-else-if="isSubmitted" class="text-secondary pt-3">Your conversation:</div>
                        </div>
                    </div>
                    <div class="mt-2">
                        <div class="collapse" :id="'feedback-' + _uid" :class="{ show: forceShowFeedback }">
                            <!-- Conversation Transcript -->
                            <div v-if="savedResponse" class="flex-grow-1">
                                <button
                                    class="btn btn-link btn-sm p-1 mb-2"
                                    @click="toggleshowConversationTranscript()"
                                >
                                    <span class="mr-1" v-show="showConversationTranscript"
                                        >Hide conversation
                                        <span class="icon icon-100 icon-glyphicons-basic-chevron-up"></span>
                                    </span>
                                    <span class="mr-1" v-show="!showConversationTranscript"
                                        >Show conversation
                                        <span class="icon icon-100 icon-glyphicons-basic-chevron-down"></span>
                                    </span>
                                </button>
                                <div v-show="showConversationTranscript" class="p-2 rounded-lg mb-3 bg-chat-convo">
                                    <chat-conversation :messages="savedResponse.messages"></chat-conversation>
                                </div>
                            </div>
                            <!-- AI-Generated Feedback -->
                            <div class="mt-4" v-if="savedResponseHasAiFeedback">
                                <text-response-ai-feedback
                                    :panel="panel"
                                    :response="savedResponse"
                                ></text-response-ai-feedback>
                            </div>
                        </div>
                    </div>
                </div>
                <audio ref="audio-player" class="d-none" autoplay="true"></audio>
            </div>
        </template>
    </base-panel>
</template>

<script>
import http from "@/axios.js"
import Utilities from "@/assets/js/utilities.js"
import BasePanel from "@/components/panels/BasePanel"
import TextResponseAiFeedback from "@/components/TextResponseAiFeedback"
import ChatConversation from "@/components/ChatConversation"

export default {
    name: "VoiceConversationPanel",
    components: {
        BasePanel,
        TextResponseAiFeedback,
        ChatConversation,
    },
    props: {
        panel: {
            type: Object,
            required: true,
        },
        savedResponses: {
            type: Array,
            required: false,
        },
        reviewMode: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            // Used to control the size of the speech audio visualizer, to create
            // a growth-animation during initialization. Starts smaller and grows larger.
            visualizerGrowthFactor: 0.2,
            // Controls the speed at which the audio visualizer grows in size.
            visualizerGrowthSpeed: 0.05,
            // A flag used to track when the visualizer's max size has been reached during the
            // intial grow-animation.
            visualizerMaxSizeReached: false,
            // Used to create audio analyzers for the AI-generated speech and microphone audio.
            audioContext: null,
            // An audio analyzer for the user's microphone, used to control the visualizer.
            micAudioAnalyzer: null,
            // A media stream source for the microphone audio.
            micAudioStream: null,
            // An audio analyzer for the AI-generated speech, used to control the visualizer.
            aiAudioAnalyzer: null,
            // The connection to the user's microphone.
            userMicrophone: null,
            // Used to determine the microphone's volume.
            micAnalyzerDataArray: null,
            // Used to determine the AI-generated speech's volume.
            aiAnalyzerDataArray: null,
            // The WebRTC peer connection to the AI speech-to-speech service.
            peerConnection: false,
            // Controls the size of the audio visualizer.
            minOuterRadius: 45,
            minInnerRadius: 30,
            maxOuterRadius: 75,
            maxInnerRadius: 60,
            // The current radius of the audio visualizer circles.
            currentOuterRadius: 45,
            currentInnerRadius: 30,
            // Controls the rate at which the radius of the audio visualizer circles change.
            animationSmoothingFactor: 0.3,
            // Used to control the color of the inner visualizer circle.
            innerCircleColor: "#115fde",
            // The key used to connect to the AI API through RTC.
            ephemeralApiKey: null,
            // The realtime model that the ephemeral key was issued for. Returned by
            // the backend so the WebRTC /v1/realtime/calls request uses the exact
            // same model — otherwise OpenAI returns "Model X does not match the
            // realtime token model".
            realtimeModel: null,
            // The current state of the conversation. Can be one of: preInit, isInitializing, isStarting,
            // isStarted, isEnded.
            currentState: "preInit",
            // Used to stop the animation loop when ending the conversation.
            stopAnimation: false,
            // Used to display to the user that the AI is speaking.
            showAiSpeaking: false,
            aiIsSpeaking: false,
            // Used to add a 'cool-off' time when displaying that the AI is speaking.
            aiSpeakingTimer: null,
            // An unordered list of transcribed messages from the conversation.
            conversationMessagesUnordered: [],
            // An ordered list of conversation item IDs that make up the conversation. Item IDs
            // are issued by the AI API.
            conversationItemIds: [],
            // Cumulative token usage across all AI response turns in the conversation.
            usageTotals: {
                total_tokens: 0,
                input_tokens: 0,
                output_tokens: 0,
                input_audio_tokens: 0,
                output_audio_tokens: 0,
            },
            // When true, AI feedback is always shown.
            forceShowFeedback: false,
            // Used to toggle visibility of the conversation transcript.
            showConversationTranscript: false,
            // The number of seconds since the conversation started.
            conversationSecondsElapsed: 0,
            // The ID of the timeout used to control the countdown timer.
            conversationTimerTimeoutId: null,
            // The timestamp (in ms) when the learner started the conversation.
            // Used to compute a precise duration when the conversation ends.
            conversationStartedAtMs: null,
            // The total length of the conversation in seconds, set when the
            // conversation ends. Reported back to the server for usage tracking.
            durationSeconds: 0,
            // Used to show the mute-state of the user's microphone.
            micIsMuted: false,
        }
    },
    computed: {
        currentSession() {
            return this.$store.getters.session
        },
        // The chronological list of messages that make up the conversation.
        conversationMessagesOrdered() {
            let vm = this
            // Use the conversation item ID list to get the list of conversations message in
            // chronological order.
            return (
                this.conversationItemIds
                    .map((itemId) => {
                        // Find the conversation message with the matching item ID.
                        return vm.conversationMessagesUnordered.find((m) => m.itemId === itemId)
                    })
                    // Remove any messages that weren't found.
                    .filter((c) => c !== undefined)
            )
        },
        isSubmitted() {
            return Boolean(this.savedResponse)
        },
        allowStopConversation() {
            return this.conversationItemIds.length >= 2
        },
        // Returns the latest saved response, if one exists.
        savedResponse() {
            if (!this.savedResponses || this.savedResponses.length == 0) {
                return null
            }
            return this.savedResponses[this.savedResponses.length - 1]
        },
        savedResponseHasAiFeedback() {
            return this.savedResponse && JSON.stringify(this.savedResponse.ai_gen_feedback) !== "{}"
        },
        submitIsAnalyzing() {
            return this.$store.getters.submitIsLoading
        },
        labelsTranslated() {
            return this.$store.getters.sessionLabelsTranslated
        },
        secondsRemaining() {
            return Math.max(this.panel.recording_time_limit_sec - this.conversationSecondsElapsed, 0)
        },
        timeRemainingFormatted() {
            return Utilities.formatStopwatch(this.secondsRemaining)
        },
    },
    watch: {
        conversationMessagesOrdered(newValue) {
            // Let the parent know that the conversation message list has updated.
            this.$emit("update:messages", newValue)
        },
        usageTotals: {
            deep: true,
            handler(newValue) {
                this.$emit("update:usageTotals", { ...newValue })
            },
        },
        durationSeconds(newValue) {
            this.$emit("update:durationSeconds", newValue)
        },
        isSubmitted(value) {
            this.$emit("update:isSubmitted", value)
            if (value) {
                // Reveal the AI feedback section.
                this.$nextTick(() => {
                    $(`#feedback-${this._uid}`).collapse("show")
                })
            }
        },
    },
    methods: {
        // Initialize the conversation.
        async initConversation() {
            this.currentState = "isInitializing"
            const keyData = await this.getEphemeralKey()
            this.ephemeralApiKey = keyData.ephemeralApiKey
            this.realtimeModel = keyData.model
            this.conversationStartedAtMs = Date.now()
            this.initializeRTC()
            // Start the countdown timer.
            this.startRecordingTimeout()
        },
        // End the conversation.
        endConversation() {
            // Make sure at least two conversation items exist before ending the conversation.
            if (!this.allowStopConversation) {
                return
            }
            this.currentState = "isEnded"
            if (this.conversationStartedAtMs) {
                this.durationSeconds = (Date.now() - this.conversationStartedAtMs) / 1000
            }
            this.destroyConversation()
            this.$emit("update:isComplete", true)
        },
        // Get an ephemeral key (and the realtime model it was issued for) to
        // connect to the AI API directly.
        getEphemeralKey() {
            return new Promise((resolve, reject) => {
                let vm = this
                let url = `/api/students/sessions/${this.currentSession.id}/panel/${this.panel.id}/conversation-key/`
                http.get(url)
                    .then((response) => {
                        resolve({
                            ephemeralApiKey: response.data.ephemeral_key,
                            model: response.data.model,
                        })
                    })
                    .catch((error) => {
                        vm.currentState = "isFailed"
                        reject(error)
                    })
            })
        },
        // Initialize the peer-to-peer RTC connection with the AI API.
        async initializeRTC() {
            try {
                // Connect to the user's microphone. Enable browser-level audio processing
                // (noise suppression, echo cancellation, auto gain) to reduce background
                // noise and echoes before audio is streamed to the AI service.
                this.userMicrophone = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        noiseSuppression: true,
                        echoCancellation: true,
                        autoGainControl: true,
                    },
                })
            } catch (error) {
                this.currentState = "isFailed"
                return
            }

            // Create audio analyzers used for the audio visualizer.
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
            this.micAudioAnalyzer = this.audioContext.createAnalyser()
            this.aiAudioAnalyzer = this.audioContext.createAnalyser()
            this.micAudioAnalyzer.fftSize = 512
            this.aiAudioAnalyzer.fftSize = 512
            // Create data arrays used to determine mic and AI-speech volume during the
            // visualizer animation.
            this.micAnalyzerDataArray = new Uint8Array(this.micAudioAnalyzer.frequencyBinCount)
            this.aiAnalyzerDataArray = new Uint8Array(this.aiAudioAnalyzer.frequencyBinCount)

            // Intializer the audio visualizer.
            this.initVisualizer()

            // Create a media stream source for the microphone audio and connect it to the
            // audio analyzer.
            this.micAudioStream = this.audioContext.createMediaStreamSource(this.userMicrophone)
            this.micAudioStream.connect(this.micAudioAnalyzer)

            // Initializer the WebRTC connection.
            this.peerConnection = new RTCPeerConnection()
            this.peerConnection.addTrack(this.userMicrophone.getTracks()[0])

            // Connect the audio player element to play the streamed audio coming from the
            // RTC connection.
            const audioEl = this.$refs["audio-player"]
            this.peerConnection.ontrack = (e) => {
                audioEl.srcObject = e.streams[0]
                // Capture the AI audio and connect it to its own audio analyzer, so we can use
                // it to control the audio visualizer.
                const aiAudioSource = this.audioContext.createMediaStreamSource(e.streams[0])
                aiAudioSource.connect(this.aiAudioAnalyzer)
            }

            // Set up a data channel for sending and receiving events from the API.
            let vm = this
            const dataChannel = this.peerConnection.createDataChannel("oai-events")
            dataChannel.addEventListener("message", (e) => {
                // Parse the message data.
                let messageData = JSON.parse(e.data)
                // Add the transcripts of both user-speech and AI-generated speech to a list. The audio
                // transcription process happens asynchronously, so these transcription events are not
                // necessarily received in the order that they happened in the actual conversation.
                if (messageData.type === "response.output_audio_transcript.done") {
                    vm.conversationMessagesUnordered.push({
                        type: "system",
                        content: messageData.transcript,
                        itemId: messageData.item_id,
                    })
                } else if (messageData.type === "conversation.item.input_audio_transcription.completed") {
                    vm.conversationMessagesUnordered.push({
                        type: "learner",
                        content: messageData.transcript,
                        itemId: messageData.item_id,
                    })
                } else if (messageData.type === "conversation.item.input_audio_transcription.failed") {
                    vm.conversationMessagesUnordered.push({
                        type: "learner",
                        content: [`[Transcription Error: ${ messageData.error }]`],
                        itemId: messageData.item_id,
                    })
                } else if (messageData.type === "conversation.item.added") {
                    // Store the IDs of new conversation items in a list. This is used to take the unordered
                    // conversation transcripts and place them in the order that they occured in the actual
                    // conversation.
                    vm.conversationItemIds.push(messageData.item.id)
                } else if (messageData.type === "response.done") {
                    const usage = messageData.response?.usage
                    if (usage) {
                        vm.usageTotals.total_tokens += usage.total_tokens ?? 0
                        vm.usageTotals.input_tokens += usage.input_tokens ?? 0
                        vm.usageTotals.output_tokens += usage.output_tokens ?? 0
                        vm.usageTotals.input_audio_tokens += usage.input_token_details?.audio_tokens ?? 0
                        vm.usageTotals.output_audio_tokens += usage.output_token_details?.audio_tokens ?? 0
                    }
                }
            })

            dataChannel.onopen = () => {
                if (vm.panel.conversation_start_mode === 'ai') {
                    // The panel is configured to have the AI start the conversation, so start in
                    // the AI-speaking state. 
                    vm.showAiSpeaking = true
                    // Send an initial empty event to start the conversation.
                    const event = { type: "response.create" }
                    dataChannel.send(JSON.stringify(event))
                    // Change the current state to started after a small delay, to make time for the
                    // initial message to trigger a response. A timeout is used because it doesn't look
                    // like the send() method returns a promise or a callback.
                    window.setTimeout(() => {
                        vm.currentState = "isStarted"
                    }, 500)
                } else {
                    // The data channel is open, so show the conversation as started.
                    vm.currentState = "isStarted"
                }
            }

            try {
                // Connect to the API over RTC using the ephemeral key.
                const offer = await this.peerConnection.createOffer()
                await this.peerConnection.setLocalDescription(offer)
                const baseUrl = "https://api.openai.com/v1/realtime/calls";
                const sdpResponse = await fetch(`${baseUrl}?model=${encodeURIComponent(this.realtimeModel)}`, {
                    method: "POST",
                    body: offer.sdp,
                    headers: {
                        Authorization: `Bearer ${this.ephemeralApiKey}`,
                        "Content-Type": "application/sdp",
                    },
                })
                const answer = { type: "answer", sdp: await sdpResponse.text() }
                await this.peerConnection.setRemoteDescription(answer)
                // Change the status of the conversation to create a smooth transition
                // from showing the start-button to showing the audio visualizer.
                this.currentState = "isStarting"
            } catch (error) {
                console.error("WebRTC connection failed", error)
                this.currentState = "isFailed"
            }
        },
        // Initialize the speech audio visualizer.
        initVisualizer() {
            // Get the canvas and context to create the animation on.
            const canvas = this.$refs.canvas
            const ctx = canvas.getContext("2d")
            // Start the canvas animation loop.
            this.animate(ctx, canvas)
        },
        // This method is called in a loop to animate the audio visualizer, by manipulating
        // the canvas element.
        animate(ctx, canvas) {
            // Used to stop the animation loop upon ending the conversation.
            if (this.stopAnimation) {
                return
            }

            // Get audio frequency data for both the microphone and AI-generated speech.
            this.micAudioAnalyzer.getByteFrequencyData(this.micAnalyzerDataArray)
            this.aiAudioAnalyzer.getByteFrequencyData(this.aiAnalyzerDataArray)
            // Get overall volume of both the microphone and AI speech.
            const micVolume =
                this.micAnalyzerDataArray.reduce((sum, val) => sum + val, 0) / this.micAnalyzerDataArray.length
            const aiVolume =
                this.aiAnalyzerDataArray.reduce((sum, val) => sum + val, 0) / this.aiAnalyzerDataArray.length

            // Assume that the AI is speaking if its volume is above a given threshold.
            const aiVolumeThreshold = 30
            const aiIsSpeaking = aiVolume > aiVolumeThreshold
            const unmuteCooldown = 1200

            if (aiIsSpeaking && this.userMicrophone) {
                // Set a flag to show that the AI is speaking.
                this.showAiSpeaking = true
                // Clear any existing unmute timers to prevent race conditions
                if (this.aiSpeakingTimer) {
                    clearTimeout(this.aiSpeakingTimer)
                    this.aiSpeakingTimer = null
                }
            } else if (!aiIsSpeaking && this.userMicrophone && this.showAiSpeaking && !this.aiSpeakingTimer) {
                this.aiSpeakingTimer = setTimeout(() => {
                    this.showAiSpeaking = false
                    this.aiSpeakingTimer = null
                }, unmuteCooldown)
            }

            // Select which volume to control the audio visualizer.
            let overallVolume = aiIsSpeaking ? aiVolume : micVolume
            // Give an extra boost to the visualizer radius while the user is speaking, as
            // the microphone audio stream seems to be less sensitive.
            let extraBoost = aiIsSpeaking ? 1 : 1.4

            // The visualizer consists of two overlapping circles - determine the target inner
            // and outer circle radii for each.
            let targetOuterRadius = Math.min(
                this.maxOuterRadius,
                Math.max(this.minOuterRadius, overallVolume * 1.4 * extraBoost)
            )
            let targetInnerRadius = Math.min(
                this.maxInnerRadius,
                Math.max(this.minInnerRadius, overallVolume * 1 * extraBoost)
            )
            // Constrain the difference in radius between the two circles.
            const maxRadiusDifference = 40
            if (targetOuterRadius - targetInnerRadius > maxRadiusDifference) {
                targetInnerRadius = targetOuterRadius - maxRadiusDifference
            }

            // This is used to make the visualizer grow outward from a smaller circle to it's full size
            // only when the visualizer is first initialized.
            if (!this.visualizerMaxSizeReached) {
                this.visualizerGrowthFactor += this.visualizerGrowthSpeed
                if (this.visualizerGrowthFactor >= 1.0) {
                    // Stop growing once full size is reached.
                    this.visualizerGrowthFactor = 1.0
                    this.visualizerMaxSizeReached = true
                }
            }
            targetOuterRadius *= this.visualizerGrowthFactor
            targetInnerRadius *= this.visualizerGrowthFactor

            // Determine the inner and outer radii to display. Use the smoothing factor to
            // gradually approach the target radius.
            this.currentOuterRadius += (targetOuterRadius - this.currentOuterRadius) * this.animationSmoothingFactor
            this.currentInnerRadius += (targetInnerRadius - this.currentInnerRadius) * this.animationSmoothingFactor

            // Determine the colors for the visualizer based on who is currently speaking.
            let targetColor = this.showAiSpeaking ? "#5a11de" : "#115fde"
            let outerCircleColorCenter = this.showAiSpeaking ? "rgba(90, 17, 222, 0.8)" : "rgba(0, 150, 255, 0.8)"
            let outerCircleColorPerimeter = this.showAiSpeaking ? "rgba(90, 17, 222, 0.08)" : "rgba(0, 150, 255, 0.08)"
            let shadowColor = this.showAiSpeaking ? "rgba(90, 17, 222, 0.8)" : "rgba(17, 95, 222, 0.7)"
            // Use a function to smoothly change the color of the inner circle.
            this.innerCircleColor = this.lerpColor(this.innerCircleColor, targetColor, 0.1)

            // Erase the canvas.
            ctx.clearRect(0, 0, canvas.width, canvas.height)

            // Determine the coordinates of the center of the canvas.
            const centerX = canvas.width / 2
            const centerY = canvas.height / 2

            // Create a color gradient effect for the fill of the inner circle.
            const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, this.currentOuterRadius)
            gradient.addColorStop(0, outerCircleColorCenter)
            gradient.addColorStop(1, outerCircleColorPerimeter)

            // Draw the outer circle.
            ctx.shadowBlur = 30
            ctx.shadowColor = outerCircleColorCenter
            ctx.beginPath()
            ctx.arc(centerX, centerY, this.currentOuterRadius, 0, Math.PI * 2)
            ctx.fillStyle = gradient
            ctx.fill()

            // Draw the inner circle.
            ctx.shadowBlur = 10
            ctx.shadowColor = shadowColor
            ctx.beginPath()
            ctx.arc(centerX, centerY, this.currentInnerRadius, 0, Math.PI * 2)
            ctx.fillStyle = this.innerCircleColor
            ctx.fill()

            // Tell the browser to run the callback function on the next repaint.
            // This is a one-shot, so we need to call this each time we want to animate a frame.
            window.requestAnimationFrame(() => this.animate(ctx, canvas))
        },
        // Perform a linear interpolation between two colors in hex format.
        lerpColor(startColor, endColor, amount) {
            const start = this.hexToRgb(startColor)
            const end = this.hexToRgb(endColor)

            // Fallback to start color in case of error.
            if (!start || !end) return startColor

            const r = Math.round(start.r + (end.r - start.r) * amount)
            const g = Math.round(start.g + (end.g - start.g) * amount)
            const b = Math.round(start.b + (end.b - start.b) * amount)
            const a = start.a + (end.a - start.a) * amount

            return `rgba(${r}, ${g}, ${b}, ${a})`
        },
        // Transform a hex color code to RGB format.
        hexToRgb(color) {
            // Handle hex format (#RRGGBB or #RGB).
            if (color.startsWith("#")) {
                let r, g, b
                if (color.length === 7) {
                    // #RRGGBB.
                    r = parseInt(color.slice(1, 3), 16)
                    g = parseInt(color.slice(3, 5), 16)
                    b = parseInt(color.slice(5, 7), 16)
                } else if (color.length === 4) {
                    // #RGB (shorthand).
                    r = parseInt(color[1] + color[1], 16)
                    g = parseInt(color[2] + color[2], 16)
                    b = parseInt(color[3] + color[3], 16)
                } else {
                    console.error("Invalid hex color format:", color)
                    return null
                }
                // Default alpha to 1
                return { r, g, b, a: 1 }
            }

            // Handle rgba() or rgb() format.
            const rgbaMatch = color.match(/rgba?\((\d+), (\d+), (\d+),? ?([\d.]*)?\)/)
            if (rgbaMatch) {
                return {
                    r: parseInt(rgbaMatch[1], 10),
                    g: parseInt(rgbaMatch[2], 10),
                    b: parseInt(rgbaMatch[3], 10),
                    a: rgbaMatch[4] ? parseFloat(rgbaMatch[4]) : 1,
                }
            }
            return null
        },
        // Show or hide the conversation transcript.
        toggleshowConversationTranscript() {
            this.showConversationTranscript = !this.showConversationTranscript
        },
        destroyConversation() {
            // Close the audio context.
            if (this.audioContext) {
                this.audioContext.close()
                this.audioContext = null
            }
            // Stop all microphone tracks.
            if (this.userMicrophone) {
                this.userMicrophone.getTracks().forEach((track) => track.stop())
                this.userMicrophone = null
            }
            // Disconnect all audio nodes.
            if (this.micAudioStream) {
                this.micAudioStream.disconnect()
                this.micAudioStream = null
            }
            // Disconnect the mic audio analyzer.
            if (this.micAudioAnalyzer) {
                this.micAudioAnalyzer.disconnect()
                this.micAudioAnalyzer = null
            }
            // Close the WebRTC peer connection if it exists.
            if (this.peerConnection) {
                this.peerConnection.getSenders().forEach((sender) => {
                    if (sender.track) sender.track.stop()
                })
                this.peerConnection.close()
                this.peerConnection = null
            }
            // Stop the animation loop.
            this.stopAnimation = true
        },
        // Start the countdown timer loop.
        startRecordingTimeout() {
            let vm = this
            this.conversationTimerTimeoutId = window.setTimeout(function () {
                // If remaining time has reached zero, stop the recording automatically.
                if (vm.secondsRemaining <= 0) {
                    vm.endConversation()
                    return
                }
                vm.conversationSecondsElapsed++
                vm.startRecordingTimeout()
            }, 1000)
        },
        setMicMuteState(state) {
            this.userMicrophone.getAudioTracks().forEach((track) => (track.enabled = !state))
            this.micIsMuted = state
        },
        handleEditAction(action) {
            this.$emit("edit-action", action)
        },
    },
    mounted() {
        if (this.savedResponse) {
            this.forceShowFeedback = true
            this.currentState = "isEnded"
        }
        if (this.reviewMode) {
            this.currentState = "isEnded"
        }
        this.$emit("update:isSubmitted", this.isSubmitted)
    },
    beforeDestroy() {
        this.destroyConversation()
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";
@import "@/assets/styles/hotspots.scss";

.visualizer-container {
    display: flex;
    justify-content: center;
    align-items: center;
}
canvas {
    background: transparent;
}
.record-btn {
    height: 70px;
    width: 70px;
}

.record-btn-container {
    transition: all 0.5s ease-in-out;
}

.record-btn-lg {
    scale: 1;
}

.record-btn-md {
    scale: 0.9;
}

.record-btn-sm {
    scale: 0.5;
}

.record-btn-icon {
    font-size: 34px;
}

.spinner {
    border: 3px solid rgba(255, 255, 255, 0.6);
    border-top-color: #fff;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    animation: spin 0.75s cubic-bezier(0.5, 0.1, 0.25, 1) infinite;
    transition: all 0.75s;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}

.active-marker {
    width: 10px;
    height: 10px;
    animation: 1.5s ease-in-out 0s infinite normal both running unex-pulse;
    transition: $transition-base;
}

@keyframes unex-pulse {
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.2);
    }
    100% {
        transform: scale(1);
    }
}

.readout {
    transition: $transition-base;
}

.bg-orang {
    background-color: #d77110;
}

.opacity-0 {
    opacity: 0;
}
</style>
